<template>
  <div class="pa-4">
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <v-card-title class="pa-0">Monitoreo de Eventos</v-card-title>
        <v-btn-toggle v-model="selectedDays" mandatory dense color="red darken-1">
          <v-btn :value="7" small>7 días</v-btn>
          <v-btn :value="15" small>15 días</v-btn>
          <v-btn :value="30" small>30 días</v-btn>
          <v-btn :value="90" small>90 días</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <v-row v-if="loadingDashboard" class="fill-height" align-content="center" justify="center">
      <v-col cols="6">
        <v-col
          class="text-subtitle-1 text-center"
          cols="12"
        >
          Consultando información por favor espere...
        </v-col>
        <v-progress-linear color="red darken-4" indeterminate rounded height="6" />
      </v-col>
    </v-row>

    <template v-if="dashboard && !loadingDashboard">
      
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <base-card :padding="true">
            <div class="d-flex align-center">
              <v-avatar size="48" color="blue lighten-5" class="mr-3">
                <v-icon color="blue">mdi-file-document-multiple</v-icon>
              </v-avatar>
              <div>
                <div class="grey--text text-caption">Total Documentos</div>
                <div class="text-h5 font-weight-bold">{{ dashboard.totals.total.toLocaleString() }}</div>
              </div>
            </div>
          </base-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <base-card :padding="true">
            <div class="d-flex align-center">
              <v-avatar size="48" color="red lighten-5" class="mr-3">
                <v-icon color="red">mdi-alert-circle</v-icon>
              </v-avatar>
              <div>
                <div class="grey--text text-caption">Total Errores</div>
                <div class="text-h5 font-weight-bold">{{ dashboard.totals.total_errors.toLocaleString() }}</div>
              </div>
            </div>
          </base-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <base-card :padding="true">
            <div class="d-flex align-center">
              <v-avatar size="48" color="orange lighten-5" class="mr-3">
                <v-icon color="orange">mdi-percent</v-icon>
              </v-avatar>
              <div>
                <div class="grey--text text-caption">Tasa de Error</div>
                <div class="text-h5 font-weight-bold">{{ errorRate }}%</div>
              </div>
            </div>
          </base-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <base-card :padding="true">
            <div class="d-flex align-center">
              <v-avatar size="48" color="green lighten-5" class="mr-3">
                <v-icon color="green">mdi-timer</v-icon>
              </v-avatar>
              <div>
                <div class="grey--text text-caption">Duración Promedio</div>
                <div class="text-h5 font-weight-bold">{{ formatDuration(dashboard.totals.avg_duration_ms) }}</div>
              </div>
            </div>
          </base-card>
        </v-col>
      </v-row>

      
      <v-row class="mt-4">
        
        <v-col cols="12" md="5">
          <base-card class="fill-height">
            <v-card-text>
              <v-card-title>
                Distribución por Estado
                <v-spacer />
              </v-card-title>
              <apexchart
                type="donut"
                height="300"
                :options="statusChartOptions"
                :series="statusChartSeries"
              />
            </v-card-text>
          </base-card>
        </v-col>

        
        <v-col cols="12" md="7">
          <base-card class="fill-height">
            <v-card-text>
              <v-card-title>
                Documentos por Proceso
                <v-spacer />
              </v-card-title>
              <apexchart
                type="bar"
                height="300"
                :options="processChartOptions"
                :series="processChartSeries"
              />
            </v-card-text>
          </base-card>
        </v-col>
      </v-row>

      
      <v-row class="mt-4">
        <v-col cols="12">
          <base-card>
            <v-card-text>
              <v-card-title>
                Actividad por Compañía
                <v-spacer />
                <v-text-field
                  v-model="companySearch"
                  append-icon="mdi-magnify"
                  label="Buscar"
                  single-line
                  hide-details
                />
                <v-spacer />
              </v-card-title>
              <v-data-table
                :headers="companyHeaders"
                :items="dashboard.by_company || []"
                :search="companySearch"
                class="elevation-1"
                @click:row="goToCompanySearch"
                style="cursor: pointer"
              >
                <template v-slot:[`item.errors`]="{ item }">
                  <v-chip
                    :color="item.errors > 0 ? 'red' : 'green'"
                    dark
                    small
                  >
                    {{ item.errors }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </base-card>
        </v-col>
      </v-row>
    </template>

    <v-alert v-if="error" type="error" dismissible class="mt-4">
      {{ error }}
    </v-alert>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatDuration, getProcessLabel, getStatusColor } from '@/data/wideEventsStatuses'

export default {
  data () {
    return {
      selectedDays: 7,
      companySearch: '',
      companyHeaders: [
        { text: 'ID', value: 'company_id', align: 'center' },
        { text: 'Compañía', value: 'company_name', align: 'center' },
        { text: 'Total Documentos', value: 'total', align: 'center' },
        { text: 'Errores', value: 'errors', align: 'center' },
      ],
    }
  },
  computed: {
    ...mapState('wideEvents', ['dashboard', 'loadingDashboard', 'error']),
    errorRate () {
      if (!this.dashboard || !this.dashboard.totals.total) return '0.0'
      return ((this.dashboard.totals.total_errors / this.dashboard.totals.total) * 100).toFixed(1)
    },
    statusChartSeries () {
      if (!this.dashboard || !this.dashboard.by_status) return []
      return this.dashboard.by_status.map(s => s.total)
    },
    statusChartOptions () {
      if (!this.dashboard || !this.dashboard.by_status) return {}
      return {
        labels: this.dashboard.by_status.map(s => s.final_status),
        colors: this.dashboard.by_status.map(s => {
          const colorMap = {
            PENDIENTE: '#9E9E9E',
            RECIBIDA: '#FF9800',
            DEVUELTA: '#FF5722',
            AUTORIZADO: '#2196F3',
            NO_AUTORIZADO: '#C62828',
            NOTIFICADO: '#4CAF50',
            NO_NOTIFICADO: '#F9A825',
            ERROR_EMAIL: '#F44336',
            ERROR_CONSTRUCCION: '#F44336',
            ERROR_FIRMA: '#F44336',
            ERROR_TOKEN: '#F44336',
            ERROR: '#B71C1C',
          }
          return colorMap[s.final_status] || '#9E9E9E'
        }),
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
      }
    },
    processChartSeries () {
      if (!this.dashboard || !this.dashboard.by_process) return []
      return [
        {
          name: 'Total',
          data: this.dashboard.by_process.map(p => p.total),
        },
        {
          name: 'Errores',
          data: this.dashboard.by_process.map(p => p.errors),
        },
      ]
    },
    processChartOptions () {
      if (!this.dashboard || !this.dashboard.by_process) return {}
      return {
        chart: { stacked: false },
        plotOptions: { bar: { horizontal: false, columnWidth: '55%' } },
        xaxis: {
          categories: this.dashboard.by_process.map(p => getProcessLabel(p.source_process)),
        },
        colors: ['#2196F3', '#F44336'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      }
    },
  },
  watch: {
    selectedDays (val) {
      this.loadDashboard()
    },
  },
  created () {
    this.loadDashboard()
  },
  methods: {
    formatDuration,
    loadDashboard () {
      this.$store.dispatch('wideEvents/getDashboard', this.selectedDays)
    },
    goToCompanySearch (item) {
      this.$router.push({
        name: 'wideEventsSearch',
        query: { company_id: item.company_id },
      })
    },
  },
}
</script>
